import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { lazy } from 'react';
import { PERMISSIONS, holdsPermission } from '@/api/permissions';
import { paths } from '@/routes/paths';
import { RequirePermission } from '@/routes/guards';
import type { ModuleManifest } from './manifest';

const OrganizationsPage = lazy(() => import('@/features/organizations/OrganizationsPage'));
const OrganizationDetailPage = lazy(() => import('@/features/organizations/OrganizationDetailPage'));
const OrganizationFormPage = lazy(() => import('@/features/organizations/OrganizationFormPage'));
const ApplicationsPage = lazy(() => import('@/features/applications/ApplicationsPage'));
const ApplicationReviewPage = lazy(() => import('@/features/applications/ApplicationReviewPage'));
const LicensesPage = lazy(() => import('@/features/licenses/LicensesPage'));

/**
 * Directory and review — the reviewer's job: the queue of registrations, every
 * organization on the platform, and the licences behind them. Platform staff
 * only; a tenant reads its own record in the top bar's organization menu.
 */
export const directoryModule: ModuleManifest = {
  key: 'directory',
  titleKey: 'modules.directory.title',
  descriptionKey: 'modules.directory.description',
  icon: ApartmentRoundedIcon,
  prefix: '/directory',
  audience: 'platform',
  anyOf: [PERMISSIONS.ORGANIZATION_REVIEW, PERMISSIONS.ORGANIZATION_READ, PERMISSIONS.LICENSE_READ],
  sections: [
    {
      key: 'directory',
      items: [
        {
          // The reviewer's primary queue, so it leads the sidebar.
          key: 'applications',
          labelKey: 'nav.applications',
          to: paths.applications,
          icon: <InboxRoundedIcon />,
          anyOf: [PERMISSIONS.ORGANIZATION_REVIEW],
        },
        {
          key: 'organizations',
          labelKey: 'nav.organizations',
          to: paths.organizations,
          icon: <ApartmentRoundedIcon />,
          anyOf: [PERMISSIONS.ORGANIZATION_READ],
        },
        {
          key: 'licenses',
          labelKey: 'nav.licenses',
          to: paths.licenses,
          icon: <VerifiedRoundedIcon />,
          anyOf: [PERMISSIONS.LICENSE_READ],
        },
      ],
    },
  ],
  routes: [
    {
      element: <RequirePermission permission={[PERMISSIONS.ORGANIZATION_READ]} />,
      children: [
        { path: paths.organizations, element: <OrganizationsPage /> },
        // The literal route has to precede the parameterised one.
        { path: paths.organizationNew, element: <OrganizationFormPage /> },
        { path: paths.organizationEdit(), element: <OrganizationFormPage /> },
        { path: paths.organization(), element: <OrganizationDetailPage /> },
      ],
    },
    {
      // The reviewer's queue: platform staff deciding on registrations.
      element: <RequirePermission permission={[PERMISSIONS.ORGANIZATION_REVIEW]} />,
      children: [
        { path: paths.applications, element: <ApplicationsPage /> },
        { path: paths.application(), element: <ApplicationReviewPage /> },
      ],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.LICENSE_READ]} />,
      children: [{ path: paths.licenses, element: <LicensesPage /> }],
    },
  ],
  landing: (viewer) =>
    holdsPermission(viewer.permissions, PERMISSIONS.ORGANIZATION_REVIEW)
      ? paths.applications
      : holdsPermission(viewer.permissions, PERMISSIONS.ORGANIZATION_READ)
        ? paths.organizations
        : paths.licenses,
};
